import showVipPrompt from '@components/ConfirmModal/showVipPrompt';
import Tabbar from '@components/Tabbar';
import Title from '@components/Title';
import IconVip from '@components/icons/IconVip';
import { useTabMain } from '@hooks/paramsHooks';
import { deleteHotspots } from '@modules/hotspot/actions/deleteHotspots';
import { modifyHotspotTItle } from '@modules/hotspot/utils/getHotspotTItle';
import { $sceneMapApi } from '@stores/dataStores';
import {
  $editingHotspot,
  $editingHotspotApi,
  type EHStore,
} from '@stores/editingHotspotStores';
import { MainPlayer } from '@stores/krpPlayerStores';
import { $loadedId } from '@stores/loadedStore';
import { $playerWidgetsApi } from '@stores/playerWidgets';
import { FeatureType, HSIconType, type Hotspot } from '@types';
import cn from '@utils/cn';
import { useStore } from 'effector-react';
import produce from 'immer';
import omit from 'ramda/es/omit';
import { useEffect, useState } from 'react';
import useConstant from 'use-constant';
import {
  getHotspotTitleId,
  hsRecentSettings,
  krpAddHotspot,
  krpRemoveHotspot,
  toggleHotspotSelected,
} from '../../hotspot';
import PanelBase from '../PanelBase';
import { ExtendHotspotSettings } from './ExtendHotspotSettings';
import { ExtendLayerSettings } from './ExtendLayerSettings';
import { HotspotBasicSettings } from './HotspotBasicSettings';

function PanelHotspot() {
  const editing = useStore($editingHotspot);
  if (!editing || editing.isBatch) return null;
  if (!editing.hotspot) return null;
  return <PanelHotspotMain editing={editing} />;
}

function PanelHotspotMain({ editing }: { editing: EHStore }) {
  const tab = useTabMain();
  const { hotspot, sceneId, isNew, sceneType } = editing;
  const firstState = useConstant(() => hotspot);
  const [basicTab, setBasicTab] = useState(0);
  const [extendTab, setExtendTab] = useState(0);

  useEffect(() => {
    return () => {
      $editingHotspotApi.clear();
      $playerWidgetsApi.clear();
    };
  }, []);

  useEffect(() => {
    return () => {
      toggleHotspotSelected({
        id: hotspot.id,
        isOn: false,
      });
    };
  }, []);

  useEffect(() => {
    const titleId = getHotspotTitleId(hotspot.id, hotspot.icon.type);
    if (titleId !== undefined) {
      const titleHotspot = modifyHotspotTItle({ ...hotspot, id: titleId });
      MainPlayer.get()?.updateDivHotspot(titleHotspot);
    }
  }, [hotspot]);

  const handleClose = () => {
    $editingHotspotApi.set(null);
  };

  const handleDelete = () => {
    if (!isNew) {
      deleteHotspots({
        sceneId,
        hotspotIds: hotspot.id,
      });
    }
    krpRemoveHotspot(hotspot.id);
  };

  const handleConfirm = () => {
    $sceneMapApi.updateScene({
      id: sceneId,
      updater: (draft) => {
        if (!draft.hotspot) {
          draft.hotspot = [] as Hotspot[];
        }
        const list = draft.hotspot;
        const index = list.findIndex((x) => x.id === hotspot.id);
        if (index > -1) {
          list[index] = hotspot;
        } else {
          list.push(hotspot);
        }
      },
    });
    // 记录上次设置的数据
    hsRecentSettings.titleSetting = hotspot.titleSetting;
    if (hotspot.data.code === FeatureType.scene) {
      hsRecentSettings.featureScene = { animation: hotspot.data.animation };
    }
    if (hotspot.icon.type === HSIconType.polygon) {
      hsRecentSettings.iconPolygon = omit(
        ['points', 'ath', 'atv'],
        hotspot.icon
      );
    }
  };

  const handleAfterClose = () => {
    if (isNew) {
      handleDelete();
    } else {
      krpRemoveHotspot(hotspot.id);
      if (tab === 'hotspot' && sceneId === $loadedId.getState()) {
        krpAddHotspot(
          produce(firstState, (draft) => {
            draft.icon.ath = hotspot.icon.ath;
            draft.icon.atv = hotspot.icon.atv;
          })
        );
      }
    }
  };

  if (!hotspot) return null;

  return (
    <PanelBase
      isChanged={() => firstState !== hotspot}
      title={<Title>{isNew ? '添加热点' : '编辑热点'}</Title>}
      onRequestClose={handleClose}
      onAfterClose={handleAfterClose}
      onConfirm={handleConfirm}
      onDeleteClick={handleDelete}
    >
      <div className="sticky top-0 z-10 bg-black pb-10">
        <Tabbar
          tabs={[
            '基础设置',
            <>
              <div key={'t'}>高级交互</div>
              <IconVip key={'v'} className="ml-5" />
            </>,
          ]}
          activeIndex={basicTab}
          onChange={(index) => {
            if (index === 1) {
              showVipPrompt();
            }
            setBasicTab(index);
            setExtendTab(0);
          }}
        />
      </div>
      <div className={cn({ hidden: basicTab !== 0 })}>
        <HotspotBasicSettings hotspot={hotspot} sceneType={sceneType} />
      </div>
      <div className={cn({ hidden: basicTab !== 1 })}>
        <Tabbar
          tabs={['热点控制', '图层控制']}
          activeIndex={extendTab}
          onChange={(index) => {
            setExtendTab(index);
          }}
        />
        <div className={cn({ hidden: extendTab !== 0 })}>
          <ExtendHotspotSettings hotspot={hotspot} />
        </div>
        <div className={cn({ hidden: extendTab !== 1 })}>
          <ExtendLayerSettings hotspot={hotspot} />
        </div>
      </div>
    </PanelBase>
  );
}

export default PanelHotspot;
